/* SEED-DEX (U9.4) — Pixel-Pokedex overlay + cartridge teaser.
 *
 * Self-contained strict-mode IIFE, no globals. Wires every [data-seed-dex-root]
 * on the page: draws a non-interactive pixel ego-graph into the cartridge well on
 * proximity, and builds a fullscreen Pokedex overlay (Tabs Daten/Terpene/Wirkung/
 * Stammbaum/Anbau) on demand. Data comes from two static Shopify-CDN JSON assets
 * (graph + profiles); the current strain photo comes from a Shopify product-image
 * URL, otherwise a procedural pixel sprite.
 *
 * SECURITY: NO innerHTML/outerHTML/insertAdjacentHTML anywhere. Every text node is
 * created via document.createElement + textContent; the graph and sprite are drawn
 * on <canvas> only. No eval / new Function / string timers / inline handlers. Any
 * network or parse failure degrades silently (no throw, no console output) — the
 * cartridge preview / skeleton simply stays.
 */
(function () {
  "use strict";

  var TYPE_HEX = { hybrid: "#C6E84A", indica: "#74e0a6", sativa: "#ffb14e", unknown: "#5a8a70" };
  var TYPE_LBL = { hybrid: "Hybrid", indica: "Indica", sativa: "Sativa" };

  // Predicate -> tab bucket. Unknown predicates fall through to the Daten tab.
  var TAB_OF = {
    strain_type: "daten", cannabinoid_thc: "daten", cannabinoid_cbd: "daten",
    seed_type: "daten", parentage: "daten", breeder: "daten", generation: "daten",
    award: "daten",
    terpene: "terpene",
    aroma: "wirkung", flavor: "wirkung", effect: "wirkung", description: "wirkung",
    flowering_time: "anbau", flowering: "anbau", yield: "anbau", height: "anbau",
    difficulty: "anbau", grow_notes: "anbau", harvest: "anbau"
  };

  var TABS = [
    { id: "daten", label: "Daten" },
    { id: "terpene", label: "Terpene" },
    { id: "wirkung", label: "Wirkung" },
    { id: "stammbaum", label: "Stammbaum" },
    { id: "anbau", label: "Anbau" }
  ];

  var reduceMotion = false;
  try { reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) {}

  // -------------------------------------------------------------------------
  // tiny DOM helpers — the ONLY way this file writes to the document.
  // -------------------------------------------------------------------------
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = String(text);
    return n;
  }
  function clear(node) { while (node.firstChild) node.removeChild(node.firstChild); }

  // -------------------------------------------------------------------------
  // shared, cached asset loaders (per URL; failures resolve to null)
  // -------------------------------------------------------------------------
  var jsonCache = {};
  function loadJson(url) {
    if (!url) return Promise.resolve(null);
    if (jsonCache[url]) return jsonCache[url];
    var p = Promise.resolve()
      .then(function () { return fetch(url, { credentials: "omit" }); })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .catch(function () { return null; });
    jsonCache[url] = p;
    return p;
  }

  // -------------------------------------------------------------------------
  // value helpers
  // -------------------------------------------------------------------------
  function typeKey(raw) {
    if (!raw) return "unknown";
    var s = String(raw).toLowerCase();
    var hasI = s.indexOf("indica") > -1, hasS = s.indexOf("sativa") > -1;
    if ((hasI && hasS) || s.indexOf("hybrid") > -1) return "hybrid";
    if (hasI) return "indica";
    if (hasS) return "sativa";
    return "unknown";
  }
  function typeHex(t) { return TYPE_HEX[t] || TYPE_HEX.unknown; }
  function firstNumber(v) {
    if (v == null) return null;
    var m = String(v).replace(",", ".").match(/-?\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : null;
  }
  function splitList(v) {
    if (v == null) return [];
    return String(v).split(/[,;/]/).map(function (s) { return s.trim(); }).filter(Boolean);
  }
  function seedOf(id) { var s = 0; for (var i = 0; i < id.length; i++) s = (s * 31 + id.charCodeAt(i)) >>> 0; return s; }
  function dexNo(id) { return (seedOf(id) % 9999) + 1; }
  function pad4(n) { var s = "000" + n; return s.slice(-4); }

  // -------------------------------------------------------------------------
  // build an internal model from the graph asset
  //   model.byId[public_id] = { id,name,type,shop,handle,no,parents[],children[] }
  //   model.byHandle[product_handle] = node
  //   model.pos[id] = {x,y}
  //   model.edges = [[parentId, childId], ...]
  // -------------------------------------------------------------------------
  function buildModel(graph) {
    var byId = {}, byHandle = {}, pos = {}, edges = [];
    var nodes = (graph && Array.isArray(graph.nodes)) ? graph.nodes : [];
    var links = (graph && Array.isArray(graph.links)) ? graph.links : [];
    nodes.forEach(function (n) {
      if (!n || !n.id) return;
      var m = {
        id: n.id,
        name: n.label || n.slug || n.id,
        type: typeKey(n.strain_type),
        shop: !!n.in_shop,
        handle: n.product_handle || null,
        thc: firstNumber(n.thc),
        aromas: Array.isArray(n.aromas) ? n.aromas.slice() : [],
        effects: Array.isArray(n.effects) ? n.effects.slice() : [],
        generation: (typeof n.generation === "number") ? n.generation : null,
        breeder: n.breeder || null,
        no: dexNo(n.id),
        parents: [],
        children: []
      };
      byId[n.id] = m;
      pos[n.id] = { x: (typeof n.x === "number" ? n.x : 0), y: (typeof n.y === "number" ? n.y : 0) };
      // Map every handle a strain's products go by (fallback: legacy single
      // product_handle for older graphs without product_handles) -> the same
      // node, so a deep-link/lookup by any variant handle resolves to it.
      // Guard: only shop-visible nodes with a primary handle expose lookup
      // keys — product_handles must never widen the surface beyond m.handle.
      var handles = (m.shop && m.handle)
        ? (Array.isArray(n.product_handles) && n.product_handles.length ? n.product_handles : [n.product_handle])
        : [];
      handles.forEach(function (h) {
        if (typeof h === "string" && h) byHandle[h] = m;
      });
    });
    links.forEach(function (l) {
      if (!l) return;
      var s = l.source, t = l.target;
      if (typeof s === "object" && s) s = s.id;
      if (typeof t === "object" && t) t = t.id;
      if (!byId[s] || !byId[t]) return;
      byId[s].children.push(t);
      byId[t].parents.push(s);
      edges.push([s, t]);
    });
    return { byId: byId, byHandle: byHandle, pos: pos, edges: edges };
  }

  // -------------------------------------------------------------------------
  // procedural pixel sprite (symmetric, seeded, type-tinted) — canvas only
  // -------------------------------------------------------------------------
  function drawSprite(canvas, m) {
    if (!canvas) return;
    var ctx = canvas.getContext("2d");
    if (!ctx) return;
    var seed = seedOf(m.id);
    function rnd() { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; }
    var GRID = 16, cell, W;
    (function fit() {
      var r = canvas.getBoundingClientRect();
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = Math.max(48, Math.floor(r.width) || 96);
      canvas.width = W * dpr; canvas.height = W * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); cell = W / GRID;
    })();
    var base = typeHex(m.type);
    function shade(hex, f) {
      var h = hex.replace("#", "");
      var r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
      r = Math.max(0, Math.min(255, Math.round(r * f)));
      g = Math.max(0, Math.min(255, Math.round(g * f)));
      b = Math.max(0, Math.min(255, Math.round(b * f)));
      return "rgb(" + r + "," + g + "," + b + ")";
    }
    var pal = [shade(base, 1.15), base, shade(base, 0.7), shade(base, 0.45)];
    ctx.fillStyle = "#0a2418"; ctx.fillRect(0, 0, W, W);
    var g = [], x, y;
    for (y = 0; y < GRID; y++) { g[y] = []; for (x = 0; x < GRID; x++) g[y][x] = 0; }
    for (y = 2; y < GRID - 2; y++) {
      for (x = 0; x < GRID / 2; x++) {
        var edge = x < 2 || y < 3 || y > GRID - 3;
        var p = edge ? 0.28 : 0.62;
        var v = rnd() < p ? (1 + Math.floor(rnd() * 3)) : 0;
        g[y][x] = v; g[y][GRID - 1 - x] = v;
      }
    }
    for (y = 0; y < GRID; y++) for (x = 0; x < GRID; x++) {
      var val = g[y][x]; if (!val) continue;
      ctx.fillStyle = pal[val - 1] || pal[1];
      ctx.fillRect(Math.floor(x * cell), Math.floor(y * cell), Math.ceil(cell), Math.ceil(cell));
    }
    ctx.fillStyle = "rgba(0,0,0,.35)";
    ctx.fillRect(Math.floor(3 * cell), Math.floor((GRID - 2) * cell), Math.floor(10 * cell), Math.ceil(cell * 0.8));
  }

  // -------------------------------------------------------------------------
  // pixel graph (canvas) — non-interactive teaser or interactive tree
  //   opts: { model, active, interactive, onSelect, ids? }
  // -------------------------------------------------------------------------
  function makeGraph(canvas, opts) {
    var ctx = canvas.getContext("2d");
    if (!ctx) return { setActive: function () {}, resize: function () {}, destroy: function () {} };
    var model = opts.model, POS = model.pos, S = model.byId;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var view = { s: 1, tx: 0, ty: 0, base: 1, w: 1, h: 1, ox: 0, oy: 0 };
    var hoverId = null, activeId = opts.active, interactive = !!opts.interactive;
    var ids = opts.ids || Object.keys(S);
    var idSet = {}; ids.forEach(function (i) { idSet[i] = 1; });
    var edges = model.edges.filter(function (e) { return idSet[e[0]] && idSet[e[1]]; });
    var destroyed = false;

    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    ids.forEach(function (id) {
      var p = POS[id]; if (!p) return;
      if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
    });
    if (!isFinite(minX)) { minX = 0; maxX = 1; minY = 0; maxY = 1; }
    var spanX = Math.max(1, maxX - minX), spanY = Math.max(1, maxY - minY);

    function degree(id) { var m = S[id]; return m ? (m.parents.length + m.children.length) : 0; }
    function resize() {
      if (destroyed) return;
      var r = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(r.width * dpr));
      canvas.height = Math.max(1, Math.floor(r.height * dpr));
      var pad = 48;
      view.base = Math.min((r.width - pad) / spanX, (r.height - pad) / spanY);
      if (!isFinite(view.base) || view.base <= 0) view.base = 1;
      view.w = r.width; view.h = r.height;
      view.ox = (r.width - spanX * view.base) / 2 - minX * view.base;
      view.oy = (r.height - spanY * view.base) / 2 - minY * view.base;
      draw();
    }
    function scr(p) { return { x: view.ox + p.x * view.base * view.s + view.tx, y: view.oy + p.y * view.base * view.s + view.ty }; }
    function rad(id) { return Math.max(7, Math.min(6 + Math.sqrt(degree(id)) * 2.2 + (S[id] && S[id].shop ? 2 : 0), 15)); }
    function anc(id) {
      if (!id || !S[id]) return [];
      var out = [id], seen = {}; seen[id] = 1; var q = [id];
      while (q.length) {
        var c = q.shift();
        (S[c].parents || []).forEach(function (p) { if (S[p] && !seen[p]) { seen[p] = 1; out.push(p); q.push(p); } });
      }
      (S[id].children || []).forEach(function (c) { if (out.indexOf(c) === -1) out.push(c); });
      return out;
    }
    function pxRect(x, y, r, fill, stroke) {
      var s2 = Math.round(r);
      ctx.fillStyle = "#06140d"; ctx.fillRect(Math.round(x - s2 - 2), Math.round(y - s2 - 2), (s2 + 2) * 2, (s2 + 2) * 2);
      ctx.fillStyle = stroke; ctx.fillRect(Math.round(x - s2 - 1), Math.round(y - s2 - 1), (s2 + 1) * 2, (s2 + 1) * 2);
      ctx.fillStyle = fill; ctx.fillRect(Math.round(x - s2 + 1), Math.round(y - s2 + 1), (s2 - 1) * 2, (s2 - 1) * 2);
    }
    function draw() {
      if (destroyed) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, view.w, view.h);
      var A = anc(activeId);
      edges.forEach(function (e) {
        if (!POS[e[0]] || !POS[e[1]]) return;
        var a = scr(POS[e[0]]), b = scr(POS[e[1]]);
        var on = A.indexOf(e[0]) > -1 && A.indexOf(e[1]) > -1;
        var act = e[0] === activeId || e[1] === activeId;
        ctx.strokeStyle = on ? "#C6E84A" : act ? "rgba(198,232,74,.4)" : "rgba(120,170,140,.18)";
        ctx.lineWidth = on ? 3 : 2; ctx.beginPath();
        var dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1;
        var cx = (a.x + b.x) / 2 + (-dy / len) * 12, cy = (a.y + b.y) / 2 + (dx / len) * 12;
        ctx.moveTo(a.x, a.y); ctx.quadraticCurveTo(cx, cy, b.x, b.y); ctx.stroke();
        var ang = Math.atan2(b.y - cy, b.x - cx), rr = rad(e[1]) + 4;
        var tx = b.x - Math.cos(ang) * rr, ty = b.y - Math.sin(ang) * rr;
        ctx.fillStyle = on ? "#C6E84A" : "rgba(120,170,140,.35)"; ctx.beginPath(); ctx.moveTo(tx, ty);
        ctx.lineTo(tx - Math.cos(ang - .5) * 7, ty - Math.sin(ang - .5) * 7);
        ctx.lineTo(tx - Math.cos(ang + .5) * 7, ty - Math.sin(ang + .5) * 7); ctx.closePath(); ctx.fill();
      });
      ids.forEach(function (id) {
        if (!POS[id] || !S[id]) return;
        var p = scr(POS[id]), r = rad(id), s = S[id], isA = id === activeId, isH = id === hoverId;
        var dim = activeId && A.indexOf(id) === -1 && !isA ? 0.4 : 1; ctx.globalAlpha = dim;
        if (isA) { ctx.shadowColor = "#C6E84A"; ctx.shadowBlur = 14; }
        pxRect(p.x, p.y, r, s.shop ? "#C6E84A" : "#173d2c", typeHex(s.type)); ctx.shadowBlur = 0;
        if (isA || isH || s.shop || degree(id) >= 4) {
          ctx.font = '8px "SdPixel", monospace'; ctx.textAlign = "center"; ctx.textBaseline = "top";
          ctx.fillStyle = "#06140d"; ctx.fillText(s.name, p.x + 1, p.y + r + 6);
          ctx.fillStyle = "#eaf6d6"; ctx.fillText(s.name, p.x, p.y + r + 5);
        }
        ctx.globalAlpha = 1;
      });
    }
    function hit(mx, my) {
      var f = null, bd = 1e9;
      ids.forEach(function (id) { if (!POS[id]) return; var p = scr(POS[id]); var d = Math.hypot(mx - p.x, my - p.y); if (d < rad(id) + 8 && d < bd) { bd = d; f = id; } });
      return f;
    }
    var onDown, onMove, onUp, onWheel, onResize;
    if (interactive) {
      var drag = false, lx = 0, ly = 0, mv = 0;
      onDown = function (e) { drag = true; mv = 0; lx = e.offsetX; ly = e.offsetY; try { canvas.setPointerCapture(e.pointerId); } catch (er) {} };
      onMove = function (e) {
        if (drag) {
          view.tx += e.offsetX - lx; view.ty += e.offsetY - ly;
          mv += Math.abs(e.offsetX - lx) + Math.abs(e.offsetY - ly); lx = e.offsetX; ly = e.offsetY; draw();
        } else {
          var h = hit(e.offsetX, e.offsetY);
          if (h !== hoverId) { hoverId = h; canvas.style.cursor = h ? "pointer" : "grab"; draw(); }
        }
      };
      onUp = function (e) { drag = false; if (mv < 5) { var h = hit(e.offsetX, e.offsetY); if (h && opts.onSelect) opts.onSelect(h); } };
      onWheel = function (e) { e.preventDefault(); var f = e.deltaY < 0 ? 1.12 : .89; view.s = Math.max(.5, Math.min(3, view.s * f)); draw(); };
      canvas.addEventListener("pointerdown", onDown);
      canvas.addEventListener("pointermove", onMove);
      canvas.addEventListener("pointerup", onUp);
      canvas.addEventListener("wheel", onWheel, { passive: false });
      canvas.style.cursor = "grab";
    }
    // Only interactive graphs (the stammbaum tab) get a global resize listener;
    // it is removed in destroy(). Non-interactive previews draw once and never
    // register a window listener (no orphaned listener holding the canvas).
    onResize = function () { resize(); };
    if (interactive) window.addEventListener("resize", onResize);
    resize();
    return {
      setActive: function (id) { activeId = id; draw(); },
      resize: resize,
      destroy: function () {
        destroyed = true;
        if (interactive) {
          window.removeEventListener("resize", onResize);
          canvas.removeEventListener("pointerdown", onDown);
          canvas.removeEventListener("pointermove", onMove);
          canvas.removeEventListener("pointerup", onUp);
          canvas.removeEventListener("wheel", onWheel);
        }
      }
    };
  }

  // ego id-set: BFS over parents+children to `depth` hops from `id`. depth 2
  // captures parents, children, grandparents, grandchildren AND siblings
  // (children of the parents) — the legible ~11-node neighbourhood of the
  // approved prototype, instead of the full 79-node universe.
  function egoIds(model, id, depth) {
    depth = depth || 2;
    var m = model.byId[id];
    if (!m) return [id];
    var out = {}; out[id] = 1;
    var frontier = [id];
    for (var d = 0; d < depth; d++) {
      var next = [];
      frontier.forEach(function (fid) {
        var node = model.byId[fid]; if (!node) return;
        node.parents.concat(node.children).forEach(function (nb) {
          if (!out[nb]) { out[nb] = 1; next.push(nb); }
        });
      });
      frontier = next;
      if (!frontier.length) break;
    }
    return Object.keys(out);
  }

  // -------------------------------------------------------------------------
  // small createElement builders
  // -------------------------------------------------------------------------
  function segsEl(val, max, amber) {
    var wrap = el("div", "sd-segs");
    var n = 12, on = Math.round((val / max) * n);
    for (var i = 0; i < n; i++) {
      wrap.appendChild(el("span", "sd-seg" + (i < on ? (" is-on" + (amber ? " is-amber" : "")) : "")));
    }
    return wrap;
  }
  function meterRow(label, val, max, valueText, amber) {
    var row = el("div", "sd-meter");
    row.appendChild(el("span", "sd-meter__k", label));
    row.appendChild(segsEl(val, max, amber));
    row.appendChild(el("span", "sd-meter__v", valueText));
    return row;
  }
  function kvRow(dl, key, value) {
    dl.appendChild(el("dt", null, key));
    dl.appendChild(el("dd", null, value));
  }
  function chipsRow(values) {
    var row = el("div", "sd-chips");
    values.forEach(function (v) { row.appendChild(el("span", "sd-chip", v)); });
    return row;
  }
  function section(title, extraClass) {
    var sec = el("section", "sd-win sd-sec" + (extraClass ? " " + extraClass : ""));
    if (title) sec.appendChild(el("h3", "sd-sec__h", title));
    return sec;
  }

  // -------------------------------------------------------------------------
  // overlay controller (one per container)
  // -------------------------------------------------------------------------
  function createDex(root, ctx) {
    var model = ctx.model, profiles = ctx.profiles;
    var productImage = root.getAttribute("data-product-image") || "";
    var productTitle = root.getAttribute("data-product-title") || "";
    var handle = root.getAttribute("data-handle") || "";
    // Anchor (current product) dex-no from Liquid (md5(handle)) so the overlay
    // entry of the current strain matches the cartridge number. Other/clicked
    // nodes keep their own dexNo(id).
    var anchorNo = root.getAttribute("data-dex-no") || null;

    var currentNode = model.byHandle[handle] || null;
    var selfId;
    if (currentNode) {
      selfId = currentNode.id;
    } else {
      selfId = "__self_" + (handle || "x");
      model.byId[selfId] = {
        id: selfId, name: productTitle || "Diese Sorte", type: "unknown",
        shop: true, handle: handle, thc: null, aromas: [], effects: [],
        generation: null, breeder: null, no: dexNo(selfId), parents: [], children: []
      };
      model.pos[selfId] = { x: 0, y: 0 };
    }

    function profileOf(id) { return (profiles && profiles.profiles && profiles.profiles[id]) || null; }
    function profileFactsOfPredicate(id, pred) {
      var prof = profileOf(id); if (!prof || !Array.isArray(prof.facts)) return [];
      return prof.facts.filter(function (f) { return f.predicate === pred; });
    }
    function factsByTab(id) {
      var out = { daten: [], terpene: [], wirkung: [], stammbaum: [], anbau: [] };
      var prof = profileOf(id);
      var facts = (prof && Array.isArray(prof.facts)) ? prof.facts : [];
      facts.forEach(function (f) {
        if (!f || !f.predicate) return;
        var bucket = TAB_OF[f.predicate] || "daten";
        out[bucket].push(f);
      });
      return out;
    }
    function hasPredicate(list, pred) { for (var i = 0; i < list.length; i++) if (list[i].predicate === pred) return true; return false; }
    function firstFactValue(list, pred) { for (var i = 0; i < list.length; i++) if (list[i].predicate === pred && list[i].value) return String(list[i].value); return null; }
    function uniq(arr) { var seen = {}, out = []; arr.forEach(function (v) { var k = v.toLowerCase(); if (!seen[k]) { seen[k] = 1; out.push(v); } }); return out; }
    function emptySection(text) { var s = el("div", "sd-screen sd-textbox sd-textbox--muted"); s.appendChild(document.createTextNode(text)); return s; }
    function typeLabel(m) { return TYPE_LBL[m.type] || null; }
    function legendItem(bg, stroke, label) {
      var d = el("div"); var i = el("i");
      i.style.background = bg; if (stroke) i.style.boxShadow = "0 0 0 2px " + stroke;
      d.appendChild(i); d.appendChild(document.createTextNode(label)); return d;
    }
    function relList(idsIn, role) {
      var wrap = el("div", "sd-rels");
      idsIn.forEach(function (rid) {
        var r = model.byId[rid]; if (!r) return;
        var btn = el("button", "sd-rel"); btn.type = "button";
        var dot = el("span", "sd-rel__d"); dot.style.background = r.shop ? "#C6E84A" : typeHex(r.type);
        btn.appendChild(dot);
        btn.appendChild(document.createTextNode(r.name + " "));
        btn.appendChild(el("small", null, role));
        btn.addEventListener("click", function () { select(rid); });
        wrap.appendChild(btn);
      });
      return wrap;
    }

    var overlay = null, tabsWrap = null, idcol = null, contentEl = null;
    var graphCtl = null, current = selfId, activeTab = "daten";
    var lastFocus = null, prevBodyOverflow = "";

    // ---- identity column ----
    function renderId(id) {
      var m = model.byId[id]; if (!m) return;
      clear(idcol);
      var spriteWrap = el("div", "sd-screen sd-sprite");
      if (id === selfId && productImage) {
        var img = el("img");
        img.src = productImage; img.alt = m.name; img.loading = "lazy"; img.decoding = "async";
        img.addEventListener("error", function () {
          if (img.parentNode === spriteWrap) spriteWrap.removeChild(img);
          var cv = el("canvas"); spriteWrap.insertBefore(cv, spriteWrap.firstChild);
          requestAnimationFrame(function () { drawSprite(cv, m); });
        });
        spriteWrap.appendChild(img);
      } else {
        var canvas = el("canvas");
        spriteWrap.appendChild(canvas);
        requestAnimationFrame(function () { drawSprite(canvas, m); });
      }
      var noText = (id === selfId && anchorNo) ? anchorNo : pad4(m.no);
      spriteWrap.appendChild(el("span", "sd-sprite__no", "Nr." + noText));
      if (m.shop) spriteWrap.appendChild(el("span", "sd-sprite__shop", "im Shop"));
      idcol.appendChild(spriteWrap);

      idcol.appendChild(el("h2", "sd-idname", m.name));

      var typerow = el("div", "sd-typerow");
      var tl = typeLabel(m);
      if (tl) { var pill = el("span", "sd-type", tl); pill.setAttribute("data-t", m.type); typerow.appendChild(pill); }
      if (m.breeder) typerow.appendChild(el("span", "sd-chip", m.breeder));
      if (typerow.firstChild) idcol.appendChild(typerow);

      if (m.shop && m.handle) {
        var a = el("a", "sd-idcta", "▶ Zum Produkt");
        a.href = "/products/" + encodeURIComponent(m.handle);
        idcol.appendChild(a);
      } else if (!m.shop) {
        idcol.appendChild(el("span", "sd-idcta sd-idcta--ghost", "Ahnen-Sorte"));
      }
    }

    // ---- tab bodies ----
    function renderTab() {
      var m = model.byId[current]; if (!m) return;
      var f = factsByTab(current);
      // Tear down any live graph controller BEFORE wiping the content — leaving
      // the stammbaum tab (or re-rendering it) must not orphan its resize /
      // pointer listeners holding the removed canvas.
      if (graphCtl && graphCtl.destroy) { graphCtl.destroy(); graphCtl = null; }
      clear(contentEl);

      if (activeTab === "daten") {
        var any = false;
        // D-074: thc_min/thc_max (bzw. cbd_*) sind die Anzeigewahrheit. Der Wert-String wird
        // NIE numerisch re-geparst — firstNumber("bis zu 24 %") waere wieder der Punktwert-
        // Kollaps, den D-045 Festlegung 6 verbietet. Traegt der Fakt die Felder, kommt der
        // Fuellwert aus max ?? min und das Label ist der operator-treue String; qualitative
        // Werte (beide null) bekommen KEIN Meter und bleiben als Stufenwort im Steckbrief.
        // Nur Alt-Fakten OHNE Felder gehen weiter durch firstNumber (blanke Skalare wie "20").
        function cannaDisplay(fact, minKey, maxKey) {
          if ((minKey in fact) || (maxKey in fact)) {
            var hi = fact[maxKey] != null ? fact[maxKey] : fact[minKey];
            return { fill: hi, label: (fact.value != null ? String(fact.value) : null) };
          }
          return { fill: firstNumber(fact.value), label: null };
        }
        var thcD = null, cbdD = null;
        f.daten.forEach(function (fact) {
          if (fact.predicate === "cannabinoid_thc") thcD = cannaDisplay(fact, "thc_min", "thc_max");
          if (fact.predicate === "cannabinoid_cbd") cbdD = cannaDisplay(fact, "cbd_min", "cbd_max");
        });
        var thc = (thcD && thcD.fill != null) ? thcD.fill : m.thc, cbd = (cbdD && cbdD.fill != null) ? cbdD.fill : null;
        if (thc != null || cbd != null) {
          var canna = section("Cannabinoide");
          if (thc != null) canna.appendChild(meterRow("THC", thc, 30, (thcD && thcD.label) || thc + "%"));
          if (cbd != null) canna.appendChild(meterRow("CBD", cbd, 5, (cbdD && cbdD.label) || (cbd < 1 ? "<1%" : cbd + "%")));
          contentEl.appendChild(canna); any = true;
        }
        var dl = el("dl", "sd-kv"), rows = 0;
        var tl = typeLabel(m); if (tl) { kvRow(dl, "Typ", tl); rows++; }
        f.daten.forEach(function (fact) {
          // Cannabinoide mit Meter bleiben aus dem Steckbrief; OHNE Zahl (qualitativ:
          // "hoch"/"niedrig") erscheinen sie hier als Wort — D-045 F4, nie numerisiert.
          if (fact.predicate === "cannabinoid_thc" && thc != null) return;
          if (fact.predicate === "cannabinoid_cbd" && cbd != null) return;
          if (fact.predicate === "strain_type") return;
          if (fact.value == null || String(fact.value).trim() === "") return;
          kvRow(dl, fact.label || fact.predicate, fact.value); rows++;
        });
        if (m.generation != null && !hasPredicate(f.daten, "generation")) { kvRow(dl, "Generation", "F" + m.generation); rows++; }
        if (rows > 0) { var sb = section("Steckbrief"); sb.appendChild(dl); contentEl.appendChild(sb); any = true; }
        if (!any) contentEl.appendChild(emptySection("Noch keine Stammdaten — folgt aus der Anreicherung."));

      } else if (activeTab === "terpene") {
        var terpVals = [];
        f.terpene.forEach(function (fact) { splitList(fact.value).forEach(function (v) { terpVals.push(v); }); });
        if (terpVals.length) {
          var ts = section("Terpen-Profil"); ts.appendChild(chipsRow(uniq(terpVals))); contentEl.appendChild(ts);
        } else {
          contentEl.appendChild(emptySection("Terpen-Daten folgen aus der Anreicherung."));
        }

      } else if (activeTab === "wirkung") {
        // Owner-Entscheid: Aroma und Geschmack sind EINE Sinnes-Facette -> zusammengefuehrt.
        var sensory = [], effects = m.effects.slice(), desc = null;
        f.wirkung.forEach(function (fact) {
          if (fact.predicate === "aroma" || fact.predicate === "flavor") splitList(fact.value).forEach(function (v) { sensory.push(v); });
          else if (fact.predicate === "effect") splitList(fact.value).forEach(function (v) { effects.push(v); });
          else if (fact.predicate === "description") { if (fact.value) desc = String(fact.value); }
        });
        if (!sensory.length && m.aromas.length) m.aromas.forEach(function (v) { sensory.push(v); }); // Fallback: Compact-Noten
        var wany = false;
        if (sensory.length) { var asec = section("Aroma & Geschmack"); asec.appendChild(chipsRow(uniq(sensory))); contentEl.appendChild(asec); wany = true; }
        if (effects.length) { var esec = section("Wirkung"); esec.appendChild(chipsRow(uniq(effects))); contentEl.appendChild(esec); wany = true; }
        if (desc) {
          var tb = el("div", "sd-screen sd-textbox");
          tb.appendChild(document.createTextNode(desc));
          if (!reduceMotion) tb.appendChild(el("span", "sd-cur"));
          contentEl.appendChild(tb); wany = true;
        }
        if (!wany) contentEl.appendChild(emptySection("Aroma-/Wirkungsprofil folgt aus der Anreicherung."));

      } else if (activeTab === "stammbaum") {
        var graphSec = el("div", "sd-screen sd-graph");
        var gc = el("canvas");
        graphSec.appendChild(gc);
        var legend = el("div", "sd-graph__legend");
        legend.appendChild(legendItem("#C6E84A", null, "im Shop"));
        legend.appendChild(legendItem("#0a2418", "#74e0a6", "Indica"));
        legend.appendChild(legendItem("#0a2418", "#ffb14e", "Sativa"));
        graphSec.appendChild(legend);
        contentEl.appendChild(graphSec);

        // (graphCtl was already destroyed at the top of renderTab)
        var hasGraph = m.parents.length || m.children.length;
        if (hasGraph) {
          // Draw ONLY the ego neighbourhood of the active strain (depth 2), fit
          // to its own bounding box (handled inside makeGraph via the `ids`
          // subset). A node click re-runs select() -> renderTab() -> a fresh
          // ego net around the clicked strain.
          graphCtl = makeGraph(gc, {
            model: model, active: current, interactive: true,
            ids: egoIds(model, current, 2),
            onSelect: function (id) { select(id); }
          });
          // Interactive hint only when there is actually a graph to navigate —
          // otherwise it overlaps the empty-state at the same absolute position.
          graphSec.appendChild(el("div", "sd-graph__hint", "Knoten tippen → Eintrag · ziehen/zoomen"));
        } else {
          graphCtl = null;
          graphSec.appendChild(el("div", "sd-graph__hint", "Keine Verwandtschaft hinterlegt"));
        }

        var relsSec = section("Verwandtschaft");
        var rendered = false;
        if (m.parents.length) {
          relsSec.appendChild(el("div", "sd-grpk", "Eltern"));
          relsSec.appendChild(relList(m.parents, "▲")); rendered = true;
        }
        if (m.children.length) {
          relsSec.appendChild(el("div", "sd-grpk", "Nachkommen (" + m.children.length + ")"));
          relsSec.appendChild(relList(m.children, "▼")); rendered = true;
        }
        if (!rendered) {
          var par = firstFactValue(profileFactsOfPredicate(current, "parentage"), "parentage");
          if (par) { relsSec.appendChild(el("p", "sd-idcross", par)); rendered = true; }
        }
        if (rendered) contentEl.appendChild(relsSec);

      } else if (activeTab === "anbau") {
        var dl2 = el("dl", "sd-kv"), r2 = 0;
        f.anbau.forEach(function (fact) {
          if (fact.value == null || String(fact.value).trim() === "") return;
          kvRow(dl2, fact.label || fact.predicate, fact.value); r2++;
        });
        if (r2 > 0) {
          var an = section("Anbau-Daten"); an.appendChild(dl2); contentEl.appendChild(an);
        } else {
          contentEl.appendChild(emptySection("Anbau-Werte (Blüte, Ertrag, Höhe, Schwierigkeit) folgen aus der Datenanreicherung."));
        }
      }

      contentEl.scrollTop = 0;
    }

    function select(id) { if (!model.byId[id]) return; current = id; renderId(id); renderTab(); }
    function setTab(t) {
      activeTab = t;
      var btns = tabsWrap.querySelectorAll(".sd-tab");
      for (var i = 0; i < btns.length; i++) btns[i].setAttribute("aria-selected", btns[i].getAttribute("data-tab") === t ? "true" : "false");
      renderTab();
    }

    // ---- overlay DOM (built once, lazily) ----
    function build() {
      overlay = el("div", "seed-dex-fs");
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      overlay.setAttribute("aria-label", "SEED-DEX");

      var scrim = el("div", "sd-fs__scrim"); scrim.setAttribute("data-close", "");
      overlay.appendChild(scrim);

      var shell = el("div", "sd-fs__shell");
      shell.appendChild(el("div", "sd-crt"));

      var bar = el("div", "sd-fs__bar");
      var title = el("span", "sd-fs__title");
      title.appendChild(el("span", "sd-led")); title.appendChild(document.createTextNode(" SEED-DEX"));
      bar.appendChild(title);
      var tools = el("div", "sd-fs__tools");
      var fullBtn = el("button", "sd-ico sd-fs__full", "⛶");
      fullBtn.type = "button"; fullBtn.setAttribute("aria-label", "Vollbild"); fullBtn.title = "Vollbild";
      fullBtn.addEventListener("click", function () { overlay.classList.toggle("is-full"); if (graphCtl) graphCtl.resize(); });
      var closeBtn = el("button", "sd-ico sd-ico--close", "✕");
      closeBtn.type = "button"; closeBtn.setAttribute("aria-label", "Schließen"); closeBtn.title = "Schließen";
      closeBtn.setAttribute("data-close", "");
      tools.appendChild(fullBtn); tools.appendChild(closeBtn);
      bar.appendChild(tools);
      shell.appendChild(bar);

      tabsWrap = el("div", "sd-tabs"); tabsWrap.setAttribute("role", "tablist");
      TABS.forEach(function (t, i) {
        var b = el("button", "sd-tab", t.label);
        b.type = "button"; b.setAttribute("role", "tab"); b.setAttribute("data-tab", t.id);
        b.setAttribute("aria-selected", i === 0 ? "true" : "false");
        b.addEventListener("click", function () { setTab(t.id); });
        tabsWrap.appendChild(b);
      });
      shell.appendChild(tabsWrap);

      var body = el("div", "sd-fs__body");
      idcol = el("aside", "sd-idcol");
      contentEl = el("section", "sd-content");
      body.appendChild(idcol); body.appendChild(contentEl);
      shell.appendChild(body);

      overlay.appendChild(shell);

      overlay.addEventListener("click", function (e) {
        var t = e.target;
        if (t && t.getAttribute && t.getAttribute("data-close") != null) close();
      });
      overlay.addEventListener("keydown", onKeydown);

      document.body.appendChild(overlay);
    }

    function focusables() {
      return Array.prototype.slice.call(
        overlay.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])')
      ).filter(function (n) { return n.offsetParent !== null || n === document.activeElement; });
    }
    function onKeydown(e) {
      if (e.key === "Escape") { e.preventDefault(); close(); return; }
      if (e.key === "Tab") {
        var f = focusables(); if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    function open() {
      if (!overlay) build();
      // Idempotent: a second (rapid double-click) open() must NOT re-capture
      // lastFocus to an element inside the already-open overlay, or the focus
      // restore on close would no longer point at the trigger button.
      if (overlay.classList.contains("is-open")) return;
      lastFocus = document.activeElement;
      // Preserve a pre-existing inline body overflow (theme / another overlay)
      // and restore that exact value on close, rather than clobbering it to "".
      prevBodyOverflow = document.body.style.overflow;
      current = selfId; activeTab = "daten";
      overlay.classList.add("is-open");
      document.body.style.overflow = "hidden";
      renderId(current); setTab("daten");
      var f = focusables();
      if (f.length) { try { f[0].focus(); } catch (er) {} }
    }
    function close() {
      if (!overlay) return;
      overlay.classList.remove("is-open"); overlay.classList.remove("is-full");
      document.body.style.overflow = prevBodyOverflow;
      if (graphCtl && graphCtl.destroy) { graphCtl.destroy(); graphCtl = null; }
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (er) {} }
    }

    return { open: open, selfId: selfId };
  }

  // -------------------------------------------------------------------------
  // per-container wiring (idempotent)
  // -------------------------------------------------------------------------
  function wire(root) {
    if (!root || root.dataset.sdWired === "1") return;
    root.dataset.sdWired = "1";

    var wrap = root.closest ? root.closest(".seed-dex-embed") : null;
    var openBtn = wrap ? wrap.querySelector("[data-sd-open]") : null;
    var well = wrap ? wrap.querySelector("[data-sd-well]") : null;
    var graphUrl = root.getAttribute("data-graph-url") || "";
    var profilesUrl = root.getAttribute("data-profiles-url") || "";
    var handle = root.getAttribute("data-handle") || "";

    var ctxPromise = null;
    function ensureCtx() {
      if (ctxPromise) return ctxPromise;
      ctxPromise = Promise.all([loadJson(graphUrl), loadJson(profilesUrl)]).then(function (res) {
        var graph = res[0];
        if (!graph) return null;
        return { model: buildModel(graph), profiles: res[1] };
      }).catch(function () { return null; });
      return ctxPromise;
    }

    function drawPreview() {
      if (root.dataset.sdPreview === "1" || !well) return;
      root.dataset.sdPreview = "1";
      ensureCtx().then(function (ctx) {
        if (!ctx) { root.dataset.sdPreview = "0"; return; }
        var node = ctx.model.byHandle[handle];
        if (!node) return;
        var ids = egoIds(ctx.model, node.id, 1);
        try { makeGraph(well, { model: ctx.model, active: node.id, interactive: false, ids: ids }); } catch (e) {}
      });
    }

    if ("IntersectionObserver" in window && well) {
      // Observe the VISIBLE cartridge canvas, not [data-seed-dex-root] — that
      // mount carries `hidden` (display:none) and would never intersect, so the
      // preview teaser would never draw.
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) { io.unobserve(en.target); drawPreview(); } });
      }, { rootMargin: "200px" });
      io.observe(well);
    } else {
      drawPreview();
    }

    var dexPromise = null;
    function ensureDex() {
      if (dexPromise) return dexPromise;
      dexPromise = ensureCtx().then(function (ctx) { return ctx ? createDex(root, ctx) : null; });
      return dexPromise;
    }
    if (openBtn) {
      openBtn.addEventListener("click", function () {
        ensureDex().then(function (dex) { if (dex) dex.open(); });
      });
    }
  }

  function init() {
    var roots = document.querySelectorAll("[data-seed-dex-root]");
    for (var i = 0; i < roots.length; i++) wire(roots[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
